import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getCommunityActionPlans } from 'apis/ActionPlansAPI';
import { getRecommendations, getSpeedToLeadMetrics } from 'apis/RecommendationsAPI';
import { ACTION_PLAN_SOURCE_FILTER_SPEED_TO_LEAD } from 'components/ActionPlans/constants';
import useRecommendationsContext from 'components/AiCompetitiveIntelligence/RecommendationsContext';
import { useSidebarMenuLogic } from 'components/layout/hooks';
import ErrorMessage from 'components/lib/ErrorMessage';
import Loader from 'components/lib/Loader';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { useAppSelector } from 'store/hook';
import { RecommendationDetailsType } from 'types/recommendations';

import {
  SPEED_TO_LEAD_CONFIG_ID,
  SPEED_TO_LEAD_FOLLOW_UP_VIEW,
  SPEED_TO_LEAD_QUERY_KEY,
  SPEED_TO_LEAD_RANKING_VIEW,
} from './constants';
import SpeedToLeadMetricsContent from './SpeedToLeadMetricsContent';
import { getOverallInsight } from './speedToLeadOverallUtils';

const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 10px;
`;

type Props = {
  recommendation?: RecommendationDetailsType;
};

const SpeedToLeadOverall = ({ recommendation }: Props) => {
  const navigate = useNavigate();
  const { company } = useRecommendationsContext();
  const { showActionPlans } = useSidebarMenuLogic();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const [followUpView, setFollowUpView] = useState<string>(SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL);
  const communityId = selectedCommunity?.id;
  const overallInsight = useMemo(
    () => getOverallInsight(recommendation, communityId, selectedCommunity?.name),
    [communityId, recommendation, selectedCommunity?.name]
  );
  const { data, isLoading, isError } = useQuery({
    queryKey: [SPEED_TO_LEAD_QUERY_KEY.METRICS, company?.id, communityId],
    queryFn: () => getSpeedToLeadMetrics(communityId ?? -1, company?.id ?? -1),
    enabled: Boolean(company?.id && communityId),
    refetchOnWindowFocus: false,
  });
  const { data: actionPlansResponse, isError: actionPlansError } = useQuery({
    queryKey: ['speed-to-lead-action-plans', communityId],
    queryFn: () =>
      getCommunityActionPlans(communityId ?? -1, {
        page: 1,
        page_size: PAGINATION_PAGE_SIZE.LARGE,
        source: ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE,
        source_categories: [ACTION_PLAN_SOURCE_FILTER_SPEED_TO_LEAD],
      }),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });
  const speedToLeadPlans = actionPlansResponse?.results ?? [];
  const { data: speedToLeadRecommendations = [] } = useQuery({
    queryKey: [SPEED_TO_LEAD_QUERY_KEY.RECOMMENDATIONS, company?.id, communityId],
    queryFn: () =>
      getRecommendations(company?.id ?? -1, {
        communities: communityId ? [communityId] : [],
        speed_to_lead: true,
      }),
    enabled: Boolean(company?.id && communityId),
    refetchOnWindowFocus: false,
  });
  const categoryRecommendationIds = useMemo(
    () => ({
      [SPEED_TO_LEAD_RANKING_VIEW.SPEED]: speedToLeadRecommendations.find(
        (item) => item.config.id === SPEED_TO_LEAD_CONFIG_ID.SPEED
      )?.id,
      [SPEED_TO_LEAD_RANKING_VIEW.QUALITY]: speedToLeadRecommendations.find(
        (item) => item.config.id === SPEED_TO_LEAD_CONFIG_ID.QUALITY
      )?.id,
    }),
    [speedToLeadRecommendations]
  );

  return (
    <Main className="mini-scroll">
      <Loader active={isLoading} />
      <ErrorMessage text="Failed to load Speed to Lead metrics. Please try again." active={isError} />
      {!isLoading && !isError && data && (
        <SpeedToLeadMetricsContent
          data={data}
          companyId={company?.id ?? -1}
          insight={overallInsight}
          primaryCommunityId={communityId ?? null}
          primaryCommunityName={selectedCommunity?.name ?? null}
          actionPlans={speedToLeadPlans}
          actionPlansEnabled={Boolean(showActionPlans)}
          actionPlansError={actionPlansError}
          followUpView={followUpView}
          onFollowUpViewChange={setFollowUpView}
          onActionPlanOpen={(planId) => navigate(`/community/${communityId}/action-plans/${planId}`)}
          categoryRecommendationIds={categoryRecommendationIds}
          onCategoryDetailsOpen={(recommendationId) =>
            navigate(`/ai-competitive-intelligence/speed-to-lead/${recommendationId}?communityId=${communityId}`)
          }
        />
      )}
    </Main>
  );
};

export default SpeedToLeadOverall;
